import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import TqaLogo from '../assets/tqa-logo.svg';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import FolderTree from './FolderTree';

const Sidebar = () => {
  const location = useLocation();
  const { projectId } = useParams();
  const { 
    projects, 
    folders,
    currentFolder,
    isCrossProjectMode,
    isConfigurationMode,
    createProject,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    moveProject,
    switchToProject,
    selectFolder
  } = useProject();
  const { user, logout } = useAuth();
  const [showNewProjectModal, setShowNewProjectModal] = React.useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState('');
  const [newFolderName, setNewFolderName] = React.useState('');
  const [selectedFolderId, setSelectedFolderId] = React.useState(null);
  const [isPersonalFolder, setIsPersonalFolder] = React.useState(false);
  const [error, setError] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  const initials = user?.name
    ? user.name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?';

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setError('');

    if (!newProjectName.trim()) {
      setError('Project name is required');
      return;
    }

    setCreating(true);
    try {
      await createProject(newProjectName.trim(), selectedFolderId);
      setShowNewProjectModal(false);
      setNewProjectName('');
      setSelectedFolderId(null);
    } catch (err) {
      setError(err.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    setError('');

    if (!newFolderName.trim()) {
      setError('Folder name is required');
      return;
    }

    if (!selectedFolderId) {
      setError('Please select a parent folder');
      return;
    }

    setCreating(true);
    try {
      let parentId = selectedFolderId;
      
      // Handle special root folder identifiers - get the actual root folder ID
      if (selectedFolderId === 'shared-root') {
        // Find the root shared folder (might need to create one if it doesn't exist)
        // For now, set to null to create at root level of shared
        parentId = null;
      } else if (selectedFolderId === 'personal-root') {
        // Get the Personal Workspace root folder ID
        const personalRoot = folders.personal && folders.personal.length > 0 
          ? folders.personal.find(f => f.name === 'Personal Workspace' && !f.parentId)
          : null;
        
        if (personalRoot) {
          parentId = personalRoot.id;
        } else {
          // If Personal Workspace doesn't exist yet, create at root with isPersonal=true
          parentId = null;
        }
      }
      
      await createFolder(newFolderName.trim(), parentId, isPersonalFolder);
      setShowNewFolderModal(false);
      setNewFolderName('');
      setSelectedFolderId(null);
      setIsPersonalFolder(false);
    } catch (err) {
      setError(err.message || 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelNewProject = () => {
    setShowNewProjectModal(false);
    setNewProjectName('');
    setSelectedFolderId(null);
    setError('');
  };

  const handleCancelNewFolder = () => {
    setShowNewFolderModal(false);
    setNewFolderName('');
    setSelectedFolderId(null);
    setIsPersonalFolder(false);
    setError('');
  };

  const handleFolderCreate = async (name, parentId = null) => {
    try {
      await createFolder(name, parentId, false);
    } catch (err) {
      console.error('Failed to create folder:', err);
      throw err; // Let FolderTree handle the error display
    }
  };

  const handleFolderRename = async (folderId, name) => {
    try {
      await updateFolder(folderId, name);
    } catch (err) {
      console.error('Failed to rename folder:', err);
      throw err; // Let FolderTree handle the error display
    }
  };

  const handleFolderDelete = async (folderId) => {
    try {
      await deleteFolder(folderId);
    } catch (err) {
      console.error('Failed to delete folder:', err);
      throw err; // Let FolderTree handle the error display
    }
  };

  const handleProjectCreateInFolder = (folderId) => {
    setSelectedFolderId(folderId);
    setShowNewProjectModal(true);
  };

  // Helper function to recursively render folder options in select dropdown
  const renderFolderOptions = (folderList, depth = 0) => {
    const options = [];
    const indent = '\u00A0\u00A0'.repeat(depth); // Non-breaking spaces for indentation

    folderList.forEach(folder => {
      options.push(
        <option key={folder.id} value={folder.id}>
          {indent}{depth > 0 ? '└ ' : ''}{folder.name}
        </option>
      );

      // Recursively add child folders
      if (folder.children && folder.children.length > 0) {
        options.push(...renderFolderOptions(folder.children, depth + 1));
      }
    });

    return options;
  };

  return (
    <aside className="sidebar w-80 p-6 flex flex-col h-screen">
      {/* Logo/Header - Fixed at top */}
      <Link to="/" className="mb-4 flex items-center space-x-3 flex-shrink-0 hover:opacity-80 transition-opacity">
        <img src={TqaLogo} alt="TQA" className="h-16 w-auto" />
        <h1 className="text-2xl brand-title">Project Hub</h1>
      </Link>
      
      {/* Cross-projects Analysis - Fixed */}
      <div className="mb-4 flex-shrink-0">
        <Link to="/cross-projects">
          <h2
            className={`p-2 text-lg font-semibold mb-2 flex items-center justify-between cursor-pointer rounded-lg hover:bg-gray-100 transition-colors duration-150 ease-in-out ${
              isCrossProjectMode
                ? 'bg-tqa-sky/10 text-tqa-blue font-medium ring-1 ring-tqa-sky/30'
                : 'text-gray-700'
            }`}
          >
            <span>Cross-projects analysis</span>
          </h2>
        </Link>
      </div>
      
      {/* Projects Header - Fixed */}
      <div className="border-t border-gray-200 pt-4 flex-shrink-0">
        <h2 className="p-2 text-lg font-semibold text-gray-700 mb-2 flex items-center justify-between">
          <span>Projects</span>
          <div className="flex gap-1">
            <button
              onClick={() => setShowNewFolderModal(true)}
              className="text-xs px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              title="New Folder"
            >
              + Folder
            </button>
            <button
              onClick={() => setShowNewProjectModal(true)}
              className="text-xs px-2 py-1 bg-tqa-blue text-white rounded hover:bg-blue-700 transition-colors"
              title="New Project"
            >
              + Project
            </button>
          </div>
        </h2>
      </div>
      
      {/* Scrollable Projects & Folders Section */}
      <div className="flex-1 overflow-y-auto min-h-0">
          {/* Shared Folders Section */}
          {folders.shared && folders.shared.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                Shared
              </h3>
              <FolderTree
                folders={folders.shared}
                projects={[]}
                currentProjectId={projectId}
                currentFolderId={currentFolder?.id}
                onProjectSelect={switchToProject}
                onFolderSelect={selectFolder}
                onFolderCreate={handleFolderCreate}
                onFolderRename={handleFolderRename}
                onFolderDelete={handleFolderDelete}
                onFolderMove={moveFolder}
                onProjectMove={moveProject}
                onProjectCreate={handleProjectCreateInFolder}
                isPersonal={false}
              />
            </div>
          )}
          
          {/* Orphaned Shared Projects (not in any folder) */}
          {folders.orphanedShared && folders.orphanedShared.length > 0 && (
            <div className="mb-4">
              {folders.shared && folders.shared.length > 0 && (
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                  Uncategorized
                </h3>
              )}
              <FolderTree
                folders={[]}
                projects={folders.orphanedShared}
                currentProjectId={projectId}
                currentFolderId={currentFolder?.id}
                onProjectSelect={switchToProject}
                onFolderSelect={selectFolder}
                onFolderCreate={handleFolderCreate}
                onFolderRename={handleFolderRename}
                onFolderDelete={handleFolderDelete}
                onFolderMove={moveFolder}
                onProjectMove={moveProject}
                onProjectCreate={handleProjectCreateInFolder}
                isPersonal={false}
              />
            </div>
          )}
          
          {/* Personal Workspace Section - Always visible */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
              Personal Workspace
            </h3>
            {folders.personal && folders.personal.length > 0 ? (
              <FolderTree
                folders={folders.personal}
                projects={[]}
                currentProjectId={projectId}
                currentFolderId={currentFolder?.id}
                onProjectSelect={switchToProject}
                onFolderSelect={selectFolder}
                onFolderCreate={handleFolderCreate}
                onFolderRename={handleFolderRename}
                onFolderDelete={handleFolderDelete}
                onFolderMove={moveFolder}
                onProjectMove={moveProject}
                onProjectCreate={handleProjectCreateInFolder}
                isPersonal={true}
              />
            ) : (
              <p className="text-xs text-gray-400 px-2 py-2">No personal folders yet</p>
            )}
          </div>
      </div>

      {/* Configuration - Fixed at bottom - Admin Only */}
      {user?.role === 'admin' && (
        <div className="border-t border-gray-200 pt-4 flex-shrink-0">
          <Link to="/configuration">
            <h2
              className={`p-2 text-lg font-semibold mb-2 flex items-center justify-between cursor-pointer rounded-lg hover:bg-gray-100 transition-colors duration-150 ease-in-out ${
                isConfigurationMode
                  ? 'bg-tqa-sky/10 text-tqa-blue font-medium ring-1 ring-tqa-sky/30'
                  : 'text-gray-700'
              }`}
            >
              <span>Configuration</span>
            </h2>
          </Link>
        </div>
      )}

      {/* User Info - Fixed at bottom */}
      {user && (
        <div className="border-t border-gray-200 pt-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name || user.email}
                className="h-10 w-10 rounded-full border border-gray-200"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold">
                {initials}
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{user.name || user.email}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="mt-3 w-full rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          >
            Sign out
          </button>
        </div>
      )}

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Project</h3>
            <form onSubmit={handleCreateProject}>
              <div className="mb-4">
                <label htmlFor="project-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="project-name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter project name"
                  autoFocus
                />
                {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
              </div>
              <div className="mb-4">
                <label htmlFor="project-folder" className="block text-sm font-medium text-gray-700 mb-2">
                  Folder <span className="text-red-600">*</span>
                </label>
                <select
                  id="project-folder"
                  value={selectedFolderId || ''}
                  onChange={(e) => setSelectedFolderId(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a folder</option>
                  {folders.shared && folders.shared.length > 0 && (
                    <optgroup label="Shared Folders">
                      {renderFolderOptions(folders.shared, 0)}
                    </optgroup>
                  )}
                  {folders.personal && folders.personal.length > 0 && (
                    <optgroup label="Personal Workspace">
                      {renderFolderOptions(folders.personal, 0)}
                    </optgroup>
                  )}
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelNewProject}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-tqa-blue rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Folder</h3>
            <form onSubmit={handleCreateFolder}>
              <div className="mb-4">
                <label htmlFor="folder-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  id="folder-name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter folder name"
                  autoFocus
                />
                {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
              </div>
              <div className="mb-4">
                <label htmlFor="folder-parent" className="block text-sm font-medium text-gray-700 mb-2">
                  Parent Folder
                </label>
                <select
                  id="folder-parent"
                  value={selectedFolderId || ''}
                  onChange={(e) => {
                    const parentId = e.target.value || null;
                    setSelectedFolderId(parentId);
                    
                    // Determine if creating a personal folder based on parent selection
                    if (parentId === 'personal-root') {
                      setIsPersonalFolder(true);
                    } else if (parentId === 'shared-root') {
                      setIsPersonalFolder(false);
                    } else if (parentId) {
                      // If selecting an existing folder, inherit its personal/shared status
                      const allFolders = [...(folders.shared || []), ...(folders.personal || [])];
                      const findFolder = (folderList) => {
                        for (const folder of folderList) {
                          if (folder.id === parentId) return folder;
                          if (folder.children) {
                            const found = findFolder(folder.children);
                            if (found) return found;
                          }
                        }
                        return null;
                      };
                      const parentFolder = findFolder(allFolders);
                      if (parentFolder) {
                        setIsPersonalFolder(parentFolder.isPersonal || false);
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  style={{ fontFamily: 'inherit' }}
                >
                  <option value="">Select a parent folder</option>
                  <option value="shared-root" style={{ fontWeight: 'bold' }}>Shared Folder</option>
                  {folders.shared && folders.shared.length > 0 && renderFolderOptions(folders.shared, 1)}
                  <option value="personal-root" style={{ fontWeight: 'bold' }}>Personal Workspace</option>
                  {folders.personal && folders.personal.length > 0 && renderFolderOptions(folders.personal, 1)}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select where to create the new folder
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelNewFolder}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Folder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;